export type CountryCode = "au" | "us" | "uk" | "ca";

interface ComponentSpec {
  label: string;
  summaryField: string;
  color: string;
}

export interface CountryConfig {
  code: CountryCode;
  name: string;
  shortName: string;
  character: string;
  characterImage: string;
  flagImage: string;
  currencySymbol: string;
  currencyCode: string;
  cohortRange: [number, number];
  defaultBirthYear: number;
  inComponents: ComponentSpec[];
  outComponents: ComponentSpec[];
  workYearTaxField: string;
  workYearCumulativeField: string;
  retYearCumulativeField: string;
  disabilityField: string | null;
}

export const COUNTRIES: Record<CountryCode, CountryConfig> = {
  us: {
    code: "us",
    name: "United States",
    shortName: "United States",
    character: "Billy Bob",
    characterImage: "/characters/billy-bob.png",
    flagImage: "/flags/us.png",
    currencySymbol: "$",
    currencyCode: "USD",
    cohortRange: [1946, 1964],
    defaultBirthYear: 1955,
    inComponents: [
      { label: "Federal income tax", summaryField: "totalIncomeTax", color: "#16a34a" },
      { label: "FICA (employee)", summaryField: "totalFicaPaid", color: "#86efac" },
    ],
    outComponents: [
      { label: "Social Security", summaryField: "totalSocialSecurityDrawn", color: "#b91c1c" },
      { label: "Medicare", summaryField: "totalMedicareSpend", color: "#dc2626" },
      { label: "Medicaid (elderly)", summaryField: "totalMedicaidElderlyShare", color: "#fca5a5" },
    ],
    workYearTaxField: "totalTaxPaid",
    workYearCumulativeField: "cumulativeTaxPaid",
    retYearCumulativeField: "cumulativeTotalSpend",
    disabilityField: "totalSsdiAttributable",
  },
  au: {
    code: "au",
    name: "Australia",
    shortName: "Australia",
    character: "Bruce",
    characterImage: "/characters/bruce.png",
    flagImage: "/flags/au.png",
    currencySymbol: "$",
    currencyCode: "AUD",
    cohortRange: [1946, 1965],
    defaultBirthYear: 1955,
    inComponents: [
      { label: "Income tax", summaryField: "totalIncomeTax", color: "#16a34a" },
      { label: "Medicare levy", summaryField: "totalMedicareLevyPaid", color: "#86efac" },
    ],
    outComponents: [
      { label: "Age Pension", summaryField: "totalPensionDrawn", color: "#b91c1c" },
      { label: "Health + aged care", summaryField: "totalGovHealthAgedCare", color: "#fca5a5" },
    ],
    workYearTaxField: "totalTaxPaid",
    workYearCumulativeField: "cumulativeTaxPaid",
    retYearCumulativeField: "cumulativeTotalSpend",
    disabilityField: null,
  },
  ca: {
    code: "ca",
    name: "Canada",
    shortName: "Canada",
    character: "Brian",
    characterImage: "/characters/brian.png",
    flagImage: "/flags/ca.svg",
    currencySymbol: "$",
    currencyCode: "CAD",
    cohortRange: [1946, 1965],
    defaultBirthYear: 1955,
    inComponents: [
      { label: "Federal tax", summaryField: "totalFedTax", color: "#15803d" },
      { label: "Provincial tax", summaryField: "totalProvTax", color: "#16a34a" },
      { label: "CPP (employee)", summaryField: "totalCppContrib", color: "#4ade80" },
      { label: "EI (employee)", summaryField: "totalEiContrib", color: "#86efac" },
    ],
    outComponents: [
      { label: "CPP retirement", summaryField: "totalCppDrawn", color: "#991b1b" },
      { label: "OAS", summaryField: "totalOasDrawn", color: "#dc2626" },
      { label: "GIS", summaryField: "totalGisDrawn", color: "#ef4444" },
      { label: "Healthcare", summaryField: "totalHealthcareSpend", color: "#fca5a5" },
      { label: "Long-term care", summaryField: "totalLongTermCare", color: "#fecaca" },
    ],
    workYearTaxField: "totalTaxPaid",
    workYearCumulativeField: "cumulativeTaxPaid",
    retYearCumulativeField: "cumulativeTotalSpend",
    disabilityField: "totalDisabilityAttributable",
  },
  uk: {
    code: "uk",
    name: "United Kingdom",
    shortName: "United Kingdom",
    character: "Basil",
    characterImage: "/characters/basil.png",
    flagImage: "/flags/uk.svg",
    currencySymbol: "£",
    currencyCode: "GBP",
    cohortRange: [1946, 1964],
    defaultBirthYear: 1955,
    inComponents: [
      { label: "Income tax", summaryField: "totalIncomeTax", color: "#16a34a" },
      { label: "National Insurance", summaryField: "totalNiPaid", color: "#86efac" },
    ],
    outComponents: [
      { label: "State Pension", summaryField: "totalStatePensionDrawn", color: "#b91c1c" },
      { label: "NHS", summaryField: "totalNhsSpend", color: "#dc2626" },
      { label: "Social care", summaryField: "totalSocialCareElderlyShare", color: "#fca5a5" },
    ],
    workYearTaxField: "totalTaxPaid",
    workYearCumulativeField: "cumulativeTaxPaid",
    retYearCumulativeField: "cumulativeTotalSpend",
    disabilityField: "totalDisabilityAttributable",
  },
};

export const COUNTRY_ORDER: CountryCode[] = ["us", "au", "ca", "uk"];

export interface CohortSummaryNorm {
  inTotal: number;
  outTotal: number;
  inComponents: { label: string; value: number; color: string }[];
  outComponents: { label: string; value: number; color: string }[];
  netBalance: number;
  govtSpendVsTaxPct: number;
  crossoverAge: number | null;
}

export interface CohortNorm {
  birthYear: number;
  retireAge: number;
  deathAge: number;
  workingYears: number;
  retirementYears: number;
  ages: number[];
  cumulativeIn: (number | null)[];
  cumulativeOut: (number | null)[];
  summary: CohortSummaryNorm;
}

interface RawCohort {
  birthYear: number;
  retireAge: number;
  deathAge: number;
  workingYears: number;
  retirementYears: number;
  summary: Record<string, number | null>;
  workYears: Record<string, number>[];
  retirementYearsData: Record<string, number | boolean>[];
}

interface RawDataset {
  cohorts: RawCohort[];
  metadata?: Record<string, unknown>;
}

export interface CountryDataset {
  config: CountryConfig;
  raw: RawDataset;
  cohortsByYear: Map<number, RawCohort>;
}

const datasetCache = new Map<CountryCode, Promise<CountryDataset>>();

export function loadCountry(code: CountryCode): Promise<CountryDataset> {
  if (!datasetCache.has(code)) {
    const config = COUNTRIES[code];
    datasetCache.set(
      code,
      fetch(`/data/${code}/dataset.json`).then(async (r): Promise<CountryDataset> => {
        if (!r.ok) throw new Error(`Failed to load ${code}: ${r.status}`);
        const raw: RawDataset = await r.json();
        const cohortsByYear = new Map<number, RawCohort>();
        for (const c of raw.cohorts) cohortsByYear.set(c.birthYear, c);
        return { config, raw, cohortsByYear };
      }),
    );
  }
  return datasetCache.get(code)!;
}

export function loadAllCountries(): Promise<CountryDataset[]> {
  return Promise.all(COUNTRY_ORDER.map(loadCountry));
}

export function normaliseCohort(ds: CountryDataset, birthYear: number): CohortNorm {
  const cohort = ds.cohortsByYear.get(birthYear);
  if (!cohort) throw new Error(`No ${ds.config.code} cohort for birth year ${birthYear}`);
  const cfg = ds.config;

  const inComponents = cfg.inComponents.map((c) => ({
    label: c.label,
    value: Number(cohort.summary[c.summaryField] ?? 0),
    color: c.color,
  }));
  const outComponents = cfg.outComponents.map((c) => ({
    label: c.label,
    value: Number(cohort.summary[c.summaryField] ?? 0),
    color: c.color,
  }));

  const inTotal = inComponents.reduce((a, b) => a + b.value, 0);
  const outTotal = outComponents.reduce((a, b) => a + b.value, 0);
  const govtSpendVsTaxPct = inTotal > 0 ? (outTotal / inTotal) * 100 : 0;
  const netBalance = outTotal - inTotal;

  let finalIn = 0;
  for (const w of cohort.workYears) {
    const cum = Number(w[cfg.workYearCumulativeField] ?? 0);
    if (cum > finalIn) finalIn = cum;
  }

  const pointsByAge = new Map<number, { in: number; out: number | null }>();

  for (const w of cohort.workYears) {
    const age = Number(w.age);
    const cum = Number(w[cfg.workYearCumulativeField] ?? 0);
    pointsByAge.set(age, { in: cum, out: null });
  }

  let crossoverAge: number | null = null;
  for (const r of cohort.retirementYearsData) {
    const age = Number(r.age);
    const cumOut = Number(r[cfg.retYearCumulativeField] ?? 0);
    const existing = pointsByAge.get(age);
    const cumIn = existing?.in ?? finalIn;
    pointsByAge.set(age, { in: cumIn, out: cumOut });
    if (crossoverAge === null && cumOut >= finalIn) crossoverAge = age;
  }

  const sortedAges = [...pointsByAge.keys()].sort((a, b) => a - b);
  const ages = sortedAges;
  const cumulativeIn: (number | null)[] = sortedAges.map((a) => pointsByAge.get(a)!.in);
  const cumulativeOut: (number | null)[] = sortedAges.map((a) => pointsByAge.get(a)!.out);

  return {
    birthYear: cohort.birthYear,
    retireAge: cohort.retireAge,
    deathAge: cohort.deathAge,
    workingYears: cohort.workingYears,
    retirementYears: cohort.retirementYears,
    ages,
    cumulativeIn,
    cumulativeOut,
    summary: {
      inTotal,
      outTotal,
      inComponents,
      outComponents,
      netBalance,
      govtSpendVsTaxPct,
      crossoverAge,
    },
  };
}

export function formatMoney(symbol: string, n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${symbol}${Math.round(n / 1_000)}k`;
  return `${symbol}${Math.round(n).toLocaleString("en-AU")}`;
}
